import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Plus, Trash2, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { isPremium } from "@/components/PremiumGate";
import { toast } from "sonner";

const WELCOME = "What would you like to cultivate today?";

export default function Tend() {
    const { user } = useAuth();
    const premium = isPremium(user);

    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [threadTitle, setThreadTitle] = useState("");
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [quota, setQuota] = useState(null);
    const endRef = useRef(null);

    const loadThreads = async () => {
        const { data } = await api.get("/tend/threads");
        setThreads(data);
    };
    const loadQuota = async () => {
        const { data } = await api.get("/tend/quota");
        setQuota(data);
    };

    useEffect(() => { loadThreads(); loadQuota(); }, []);

    const openThread = async (id) => {
        setActiveId(id);
        setMessages([]);
        const { data } = await api.get(`/tend/threads/${id}`);
        setThreadTitle(data.thread?.title || "");
        setMessages(data.messages || []);
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 100);
    };

    const newThread = async () => {
        const { data } = await api.post("/tend/threads", { title: null });
        await loadThreads();
        setActiveId(data.thread_id);
        setMessages([]);
        setThreadTitle(data.title);
    };

    const deleteThread = async (id) => {
        await api.delete(`/tend/threads/${id}`);
        if (activeId === id) { setActiveId(null); setMessages([]); }
        await loadThreads();
    };

    const send = async () => {
        const content = draft.trim();
        if (!content) return;

        // Free-tier local guard
        if (quota && !quota.premium && quota.remaining <= 0) {
            toast.error("You've reached this month's Tend limit. Premium unlocks unlimited reflection.");
            return;
        }

        let threadId = activeId;
        if (!threadId) {
            const { data } = await api.post("/tend/threads", { title: null });
            threadId = data.thread_id;
            setActiveId(threadId);
            await loadThreads();
        }

        // Optimistic user message
        const optimistic = {
            message_id: "tmp_" + Date.now(),
            thread_id: threadId,
            role: "user",
            content,
            created_at: new Date().toISOString(),
        };
        setMessages((m) => [...m, optimistic]);
        setDraft("");
        setSending(true);
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);

        try {
            const { data } = await api.post(`/tend/threads/${threadId}/messages`, { content });
            setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id).concat([data.user_message, data.assistant_message]));
            await loadQuota();
            await loadThreads();
        } catch (e) {
            setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
            if (e.response?.status === 402) {
                toast.error("You've reached this month's Tend limit. Premium unlocks unlimited reflection.");
                loadQuota();
            } else {
                toast.error("Something quieted the response. Please try again.");
            }
        } finally {
            setSending(false);
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 200);
        }
    };

    const remaining = quota?.remaining;
    const outOfQuota = quota && !quota.premium && remaining <= 0;

    return (
        <div className="grid md:grid-cols-[280px_1fr] gap-8 min-h-[70vh]">
            {/* Thread list */}
            <aside className="hidden md:flex flex-col" data-testid="tend-sidebar">
                <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground mb-4">Tend</div>
                <button
                    onClick={newThread}
                    data-testid="new-thread-btn"
                    className="text-left hairline rounded-md p-3 mb-3 hover:border-foreground/25 transition-colors flex items-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" strokeWidth={1.5} /> New reflection
                </button>
                <div className="space-y-1 overflow-y-auto flex-1 pr-1">
                    {threads.length === 0 && <div className="text-xs text-muted-foreground px-2">No reflections yet.</div>}
                    {threads.map((t) => (
                        <div key={t.thread_id} className="group flex items-center gap-1">
                            <button
                                onClick={() => openThread(t.thread_id)}
                                data-testid={`thread-${t.thread_id}`}
                                className={`flex-1 text-left px-3 py-2 rounded-md text-sm truncate transition-colors ${activeId === t.thread_id ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.04] text-muted-foreground"}`}
                            >
                                {t.title || "A quiet reflection"}
                            </button>
                            <button
                                onClick={() => deleteThread(t.thread_id)}
                                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1"
                                aria-label="Delete reflection"
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="mt-6 text-[11px] text-muted-foreground border-t border-foreground/10 pt-4">
                    {quota?.premium ? (
                        <span>Unlimited reflection · Premium</span>
                    ) : quota ? (
                        <span>{remaining} of {quota.limit} reflections remain this month</span>
                    ) : null}
                </div>
            </aside>

            {/* Conversation column */}
            <section className="flex flex-col" data-testid="tend-panel">
                {/* Header */}
                <div className="mb-8">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-3 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" strokeWidth={1.5} /> A quiet garden for reflection
                    </div>
                    <h1 className="font-serif text-4xl md:text-6xl leading-[1.02] font-light">
                        {WELCOME}
                    </h1>
                    <p className="mt-4 text-sm text-muted-foreground max-w-xl leading-relaxed">
                        Tend is a quiet space to sit with what's cultivating in you. It is not a chatbot and does
                        not speak for God — it offers thoughtful questions and points you toward Scripture, prayer,
                        and faithful reflection.
                    </p>
                </div>

                {/* Messages — no chat bubbles; journal-like typography */}
                <div className="flex-1 space-y-10 pb-8">
                    <AnimatePresence initial={false}>
                        {messages.map((m) => (
                            <motion.div
                                key={m.message_id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                data-testid={`msg-${m.role}`}
                                className="max-w-3xl"
                            >
                                {m.role === "user" ? (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground mb-2">You wrote</div>
                                        <p className="text-lg leading-relaxed whitespace-pre-wrap">{m.content}</p>
                                    </div>
                                ) : (
                                    <div className="border-l border-[hsl(var(--eden-gold))]/40 pl-6">
                                        <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(var(--eden-gold))] mb-3">A quiet reply</div>
                                        <div className="font-serif text-xl md:text-2xl leading-relaxed whitespace-pre-wrap">
                                            {m.content}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                    {sending && (
                        <div className="max-w-3xl border-l border-[hsl(var(--eden-gold))]/40 pl-6" data-testid="tend-thinking">
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(var(--eden-gold))] mb-3">Listening…</div>
                            <div className="flex gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--eden-gold))] animate-pulse" />
                                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--eden-gold))]/70 animate-pulse" style={{ animationDelay: "150ms" }} />
                                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--eden-gold))]/50 animate-pulse" style={{ animationDelay: "300ms" }} />
                            </div>
                        </div>
                    )}
                    <div ref={endRef} />
                </div>

                {/* Composer */}
                {outOfQuota ? (
                    <div className="hairline rounded-lg p-8 md:p-10" data-testid="tend-out-of-quota">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4">
                            <Lock className="w-3 h-3" strokeWidth={1.5} /> Premium
                        </div>
                        <h3 className="font-serif text-3xl leading-tight max-w-2xl">
                            You've filled this month's reflections. Premium opens Tend without limit.
                        </h3>
                        <Link to="/membership">
                            <Button className="mt-6 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md h-11 px-6 text-xs uppercase tracking-[0.3em]" data-testid="tend-upgrade-btn">
                                See Premium
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="hairline rounded-lg p-4 md:p-5 max-w-3xl">
                        <Textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
                            placeholder="What's on your mind? A tension, a question, a quiet joy…"
                            rows={3}
                            data-testid="tend-composer"
                            disabled={sending}
                            className="border-none bg-transparent resize-none focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none p-0 text-base leading-relaxed"
                        />
                        <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                                {quota?.premium ? "Unlimited" : quota ? `${remaining} left this month` : ""}
                            </div>
                            <Button
                                onClick={send}
                                disabled={sending || !draft.trim()}
                                data-testid="tend-send-btn"
                                className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md text-xs uppercase tracking-[0.3em] h-10 px-5"
                            >
                                {sending ? "Sending…" : "Send reflection"}
                            </Button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
